import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useWindowScroll, useWindowSize } from 'react-use';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

import { useDetectOutsideClick, useTranslation } from '@hooks';
import { useAuth } from '@context/AuthContext';
import { useCart } from '@context/CartContext';
import { useNotification } from '@context/NotificationContext';
import { BackIcon } from '@components/icons';
import { H2, Text2, Caption2, Subtitle2 } from '@components/styles/Typography';

import {
    AddCoupon,
    CartItem,
    CartSummary,
    CartMobileMenu,
    CartPlaceholder,
    MobileCartModal,
} from '@components/cart';
import {
    HideTablet,
    StyledColumn,
    StyledRow,
} from '@components/styles/CommonStyles';
import { NewButton, NullProductCard, TrendOfWeeks } from '@components/ui';

import * as gtag from '../lib/gtag';

// TODO: check the notification
const CartPage = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const { currentUser } = useAuth();
    const { getCartQuery, updateCookie } = useCart();
    const modalContainerRef = useRef();
    const { data, isLoading } = getCartQuery;

    const {
        products,
        warning,
        total,
        discounts,
        subtotal,
        extra,
        coupon,
        cargo_fee: cargoFee,
        total_item_cnt: itemCount,
    } = data?.data || {
        products: [],
        warning: [],
    };

    const handleOpen = () => {
        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    useDetectOutsideClick(modalContainerRef, handleClose);

    useEffect(() => {
        if (!isLoading) {
            gtag.event({
                action: 'view_cart',
                currency: 'TRY',
                items: products,
                value: total,
            });
        }
    }, [products, total, isLoading]);

    const { y } = useWindowScroll();
    const { width } = useWindowSize();
    const isScrolling = y > 30;

    const { open } = useNotification();

    useEffect(() => {
        if (warning?.COUPON?.message) {
            open({
                type: 'warning',
                description: warning.COUPON?.message,
            });
        }
    }, [warning]);

    useEffect(() => {
        if (router?.query?.basketHash) {
            updateCookie(router.query.basketHash);
        }
    }, [router?.query.basketHash]);
    const productCount = data?.data?.total_item_cnt;
    return (
        <>
            <NextSeo title={`${t('ferventeTitle')} ${t('cartTitle')}`} />

            <CartPageContainer>
                {isLoading ? (
                    <>
                        <CartMobileHeader>
                            <BackIcon onClick={() => router.back()} />
                            <StyledColumn>
                                <Subtitle2 uppercase>
                                    {t('cartTitle')}
                                </Subtitle2>
                                <Text2>
                                    {itemCount} {t('product')}
                                </Text2>
                            </StyledColumn>
                        </CartMobileHeader>
                        <CartPlaceholder />
                    </>
                ) : (
                    <>
                        <CartMobileHeader isScrolling={isScrolling}>
                            <BackIcon onClick={() => router.back()} />
                            <StyledColumn>
                                <Subtitle2 uppercase>
                                    {t('cartTitle')}
                                </Subtitle2>
                                <Text2>
                                    {itemCount} {t('product')}
                                </Text2>
                            </StyledColumn>
                        </CartMobileHeader>
                        {productCount > 0 ? (
                            <CartPageWrapper>
                                <CartPageProductContainer>
                                    <HideTablet>
                                        <StyledRow width="820px">
                                            <StyledRow width="max-content">
                                                <H2 uppercase>
                                                    {t('cartTitle')}
                                                </H2>
                                                <Text2>
                                                    ({itemCount} {t('product')})
                                                </Text2>
                                            </StyledRow>
                                            <NewButton
                                                type="secondary"
                                                size="large"
                                                content={t(
                                                    'cartContinueShopping',
                                                )}
                                                handleClick={() =>
                                                    router.push('/wholesale-clothing')
                                                }
                                            />
                                        </StyledRow>
                                    </HideTablet>
                                    <CartItemWrapper>
                                        {products?.map((product) => (
                                            <CartItem
                                                product={product}
                                                key={product.id}
                                                stock={product.stock}
                                            />
                                        ))}
                                    </CartItemWrapper>
                                    <BottomWrapper>
                                        <AddCoupon
                                            couponName={coupon?.campaign?.name}
                                            disabled={Number(total) === 0}
                                        />
                                    </BottomWrapper>
                                </CartPageProductContainer>
                                <CartSummary
                                    currentUser={currentUser}
                                    discounts={discounts}
                                    extras={extra}
                                    subtotal={subtotal}
                                    total={total}
                                    cargoFee={cargoFee}
                                />
                            </CartPageWrapper>
                        ) : (
                            <>
                                <HideTablet width="100%" maxWidth="1080px">
                                    <StyledRow>
                                        <StyledRow width="max-content">
                                            <H2 uppercase>{t('cartTitle')}</H2>
                                            <Caption2>
                                                ({itemCount} {t('product')})
                                            </Caption2>
                                        </StyledRow>
                                    </StyledRow>
                                </HideTablet>
                                <EmptyCartContainer>
                                    <NullProductCard
                                        buttonTitle={t('cartContinueShopping')}
                                        photo="https://cloud.keikei.com/cdn/banners/sepet.png"
                                        text={t('cartNoProduct')}
                                    />
                                    <TrendOfWeeksWrapper>
                                        <TrendOfWeeks />
                                    </TrendOfWeeksWrapper>
                                </EmptyCartContainer>
                            </>
                        )}
                        {productCount > 0 && (
                            <>
                                <CartMobileMenu
                                    handleOpen={handleOpen}
                                    currentUser={currentUser}
                                    total={total}
                                />

                                {width < 768 && (
                                    <MobileCartModal
                                        isOpen={isOpen}
                                        extras={extra}
                                        discounts={discounts}
                                        handleClose={handleClose}
                                        subtotal={subtotal}
                                        modalContainerRef={modalContainerRef}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </CartPageContainer>
        </>
    );
};
const CartPageContainer = styled.div`
    margin: 200px auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    background-color: ${({ theme }) => theme.colors.secondary};
    @media (max-width: 1024px) {
        margin: 20px 0;
        padding-bottom: 120px;
    }
`;
const CartPageWrapper = styled.div`
    display: grid;
    justify-items: center;
    position: relative;
    grid-template-columns: 2fr 1fr;
    gap: 20px;
    max-width: 1080px;

    h2 {
        margin-right: 4px;
    }

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        padding-bottom: 120px;
        width: 100%;
    }
`;

const CartPageProductContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    @media (max-width: 1025px) {
        display: block;
        margin-top: 41px;
        width: 100%;
    }
`;
const CartItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    border: ${({ theme }) => `1px solid ${theme.colors.bgGray10}`};
    margin: 20px 0;

    @media (max-width: 1024px) {
        border: none;
    }
`;

const BottomWrapper = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;

    @media (max-width: 1024px) {
        flex-direction: column;
    }
`;

const CartMobileHeader = styled.div`
    display: flex;
    justify-content: center;
    padding: 10px 20px;
    align-items: center;
    top: ${({ isScrolling }) => (isScrolling ? '0' : '0')};
    transition: all 0.2s ease-in-out;
    left: 0;
    background: ${({ transparentBackground, theme }) =>
        transparentBackground ? 'transparent' : theme.colors.secondary};
    z-index: 9;
    position: fixed;
    width: 100%;
    box-shadow: 0 2px 2px 0 rgb(40 49 59 / 5%);

    @media (min-width: 1025px) {
        display: none;
    }
`;
const EmptyCartContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 98px;
`;
const TrendOfWeeksWrapper = styled.div`
    @media (max-width: 1024px) {
        margin-top: 80px;
    }
`;

export default CartPage;
